import { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { Monitor, Smartphone, Tablet, Globe, Radio, Users, ShoppingCart, Clock } from "lucide-react";

const FLAG = (cc) => {
  if (!cc || cc.length !== 2 || cc === "??") return "🌐";
  return String.fromCodePoint(...[...cc.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)));
};
const deviceIcon = (d) => (d === "mobile" ? Smartphone : d === "tablet" ? Tablet : Monitor);

function Stat({ label, value, accent, testid, pulse }) {
  return (
    <div className="card-lux p-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        {pulse && <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-70" style={{ background: "#38d66b" }} /><span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: "#38d66b" }} /></span>}
        <p className="overline" style={{ fontSize: "0.55rem" }}>{label}</p>
      </div>
      <p className="font-display text-4xl" style={{ color: accent || "var(--lo-gold)" }}>{value}</p>
    </div>
  );
}

export function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [live, setLive] = useState({ visitors: [], count: 0 });
  const [carts, setCarts] = useState({ carts: [], count: 0, window_days: 7 });
  const [err, setErr] = useState(false);
  const timer = useRef(null);

  const load = async () => {
    try {
      const [o, l, c] = await Promise.all([
        api.get("/admin/analytics/overview"),
        api.get("/admin/analytics/live"),
        api.get("/admin/analytics/carts"),
      ]);
      setData(o.data); setLive(l.data); setCarts(c.data); setErr(false);
    } catch { setErr(true); }
  };

  useEffect(() => {
    load();
    timer.current = setInterval(load, 5000);
    return () => clearInterval(timer.current);
  }, []);

  if (!data) return <p className="text-sm" style={{ color: "var(--lo-muted)" }}>{err ? "Erreur de chargement des statistiques." : "Chargement des statistiques..."}</p>;

  const maxEvo = Math.max(1, ...data.evolution.map((e) => e.count));
  const maxDev = Math.max(1, ...data.devices.map((d) => d.count));

  return (
    <div className="space-y-8" data-testid="analytics-panel">
      <div className="flex items-center gap-2">
        <Radio size={14} stroke="#38d66b" />
        <p className="overline" style={{ fontSize: "0.55rem", color: "var(--lo-muted)" }}>Actualisation automatique toutes les 5 secondes</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Stat label="En ligne maintenant" value={data.online} accent="#38d66b" pulse testid="stat-online" />
        <Stat label={`Actifs (${Math.round(data.windows.active_seconds / 60)} min)`} value={data.active} testid="stat-active" />
        <Stat label="Aujourd'hui" value={data.today} testid="stat-today" />
        <Stat label="7 jours" value={data.last7} testid="stat-7d" />
        <Stat label="30 jours" value={data.last30} testid="stat-30d" />
      </div>

      <div className="card-lux p-6" data-testid="active-carts">
        <div className="flex items-center justify-between gap-4 mb-6">
          <p className="overline mb-0 flex items-center gap-2"><ShoppingCart size={13} stroke="var(--lo-gold)" /> Paniers actifs ({carts.count})</p>
          <span className="text-[10px] uppercase tracking-widest" style={{ color: "var(--lo-muted)" }}>7 derniers jours</span>
        </div>
        {carts.carts.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--lo-muted)" }}>Aucun panier actif pour le moment.</p>
        ) : (
          <div className="space-y-3 max-h-96 overflow-auto">
            {carts.carts.map((cart) => {
              const I = deviceIcon(cart.device);
              const updated = cart.updated_at ? new Date(cart.updated_at) : null;
              const seconds = updated ? Math.max(0, Math.floor((Date.now() - updated.getTime()) / 1000)) : null;
              const age = seconds === null ? "—" : seconds < 60 ? `il y a ${seconds}s` : seconds < 3600 ? `il y a ${Math.floor(seconds / 60)} min` : `il y a ${Math.floor(seconds / 3600)} h`;
              return (
                <div key={cart.visitor_id} className="border rounded-lg p-4" style={{ borderColor: "var(--lo-border)" }} data-testid={`cart-${cart.visitor_id}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <ShoppingCart size={15} stroke="var(--lo-gold)" className="shrink-0" />
                    <span className="font-mono text-xs" style={{ color: "var(--lo-muted)" }}>Visiteur {cart.visitor_id}</span>
                    <I size={14} stroke="var(--lo-gold)" className="shrink-0" />
                    <span>{FLAG(cart.country)}</span>
                    <span className="ml-auto flex items-center gap-1 text-xs shrink-0" style={{ color: "var(--lo-muted)" }}><Clock size={12} /> {age}</span>
                  </div>
                  <div className="space-y-1.5">
                    {cart.items.map((item, idx) => (
                      <div key={`${cart.visitor_id}-${item.key || idx}`} className="flex items-center justify-between gap-3 text-sm">
                        <span className="truncate" style={{ color: "var(--lo-text)" }}>{item.name} <span style={{ color: "var(--lo-muted)" }}>× {item.qty}</span></span>
                        <span className="shrink-0" style={{ color: "var(--lo-gold)" }}>{(Number(item.unit || 0) * Number(item.qty || 0)).toFixed(2)} €</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t flex items-center justify-between text-xs" style={{ borderColor: "var(--lo-border)" }}>
                    <span style={{ color: "var(--lo-muted)" }}>{cart.units} article{cart.units > 1 ? "s" : ""} · {cart.path}</span>
                    <span className="font-display text-lg" style={{ color: "var(--lo-gold)" }}>{Number(cart.subtotal || 0).toFixed(2)} €</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-lux p-6">
          <p className="overline mb-6">Évolution (14 jours)</p>
          <div className="flex items-end gap-1.5 h-40" data-testid="evolution-chart">
            {data.evolution.map((e) => (
              <div key={e.date} className="flex-1 flex flex-col items-center gap-1 group">
                <span className="text-[9px] opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "var(--lo-gold)" }}>{e.count}</span>
                <div className="w-full rounded-t transition-all" style={{ height: `${(e.count / maxEvo) * 100}%`, minHeight: e.count > 0 ? 4 : 1, background: "linear-gradient(180deg,#E7C979,#A9812F)" }} />
                <span className="text-[8px]" style={{ color: "var(--lo-muted)" }}>{e.date.slice(8)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card-lux p-6">
          <p className="overline mb-6">Pages actuellement consultées</p>
          {data.current_pages.length === 0 ? <p className="text-sm" style={{ color: "var(--lo-muted)" }}>Aucun visiteur actif.</p> : (
            <div className="space-y-2.5" data-testid="current-pages">
              {data.current_pages.map((p) => (
                <div key={p.path} className="flex items-center justify-between text-sm">
                  <span className="truncate mr-3" style={{ color: "var(--lo-text)" }}>{p.path}</span>
                  <span className="font-display text-lg shrink-0" style={{ color: "var(--lo-gold)" }}>{p.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-lux p-6">
          <p className="overline mb-6">Type d'appareil (7 j)</p>
          <div className="space-y-3" data-testid="device-breakdown">
            {data.devices.map((d) => { const I = deviceIcon(d.key); return (
              <div key={d.key} className="flex items-center gap-3">
                <I size={16} stroke="var(--lo-gold)" />
                <span className="text-sm capitalize w-20" style={{ color: "var(--lo-text)" }}>{d.key}</span>
                <div className="flex-1 h-2 rounded" style={{ background: "rgba(255,255,255,0.06)" }}>
                  <div className="h-full rounded" style={{ width: `${(d.count / maxDev) * 100}%`, background: "linear-gradient(90deg,#A9812F,#E7C979)" }} />
                </div>
                <span className="text-sm w-8 text-right" style={{ color: "var(--lo-muted)" }}>{d.count}</span>
              </div>
            ); })}
          </div>
        </div>

        <div className="card-lux p-6">
          <p className="overline mb-6 flex items-center gap-2"><Globe size={13} stroke="var(--lo-gold)" /> Pays (7 j)</p>
          {data.countries.every((c) => c.key === "??") ? (
            <p className="text-xs leading-relaxed" style={{ color: "var(--lo-muted)" }}>Géolocalisation indisponible sur cet hébergement (aucun en-tête pays). Aucune adresse IP n'est stockée (RGPD). Elle s'activera automatiquement derrière un CDN fournissant l'en-tête pays.</p>
          ) : (
            <div className="grid grid-cols-2 gap-2" data-testid="country-breakdown">
              {data.countries.map((c) => (
                <div key={c.key} className="flex items-center justify-between text-sm">
                  <span style={{ color: "var(--lo-text)" }}>{FLAG(c.key)} {c.key}</span>
                  <span style={{ color: "var(--lo-gold)" }}>{c.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="card-lux p-6">
        <p className="overline mb-6 flex items-center gap-2"><Users size={13} stroke="var(--lo-gold)" /> Visiteurs actifs en direct ({live.count})</p>
        {live.visitors.length === 0 ? <p className="text-sm" style={{ color: "var(--lo-muted)" }}>Aucun visiteur actif pour le moment.</p> : (
          <div className="space-y-2 max-h-72 overflow-auto" data-testid="live-visitors">
            {live.visitors.map((v, i) => { const I = deviceIcon(v.device); return (
              <div key={i} className="flex items-center gap-3 text-sm py-1.5 border-b" style={{ borderColor: "var(--lo-border)" }}>
                <span className="relative flex h-2 w-2 shrink-0">{v.online && <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-70" style={{ background: "#38d66b" }} />}<span className="relative inline-flex rounded-full h-2 w-2" style={{ background: v.online ? "#38d66b" : "#666" }} /></span>
                <span className="font-mono text-xs" style={{ color: "var(--lo-muted)" }}>{v.id}</span>
                <I size={14} stroke="var(--lo-gold)" className="shrink-0" />
                <span>{FLAG(v.country)}</span>
                <span className="flex-1 truncate" style={{ color: "var(--lo-text)" }}>{v.path}</span>
                <span className="text-xs shrink-0" style={{ color: "var(--lo-muted)" }}>il y a {v.seconds_ago}s</span>
              </div>
            ); })}
          </div>
        )}
      </div>
    </div>
  );
}
